import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Upload,
  FileSpreadsheet,
  Building2,
  CheckCircle2,
  XCircle,
  Trash2,
  Eye,
  Search,
  Filter,
  LogOut,
  Shield,
  Users,
  TrendingUp,
  Clock,
  AlertTriangle,
  Check,
  X,
  RefreshCw,
  CloudDownload,
  CloudUpload,
  Database,
  Zap,
  Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MarketDemandWidget from "@/components/admin/MarketDemandWidget";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [authHeader, setAuthHeader] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Dashboard data
  const [businesses, setBusinesses] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // CSV Upload
  const [isDragging, setIsDragging] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Actions
  const [actionLoading, setActionLoading] = useState({});

  // POD Sync
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState({ businesses: false, content: false, all: false });
  const [syncResult, setSyncResult] = useState(null);

  // Business Submissions
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  // Check for saved auth
  useEffect(() => {
    const savedAuth = localStorage.getItem("adminAuth");
    if (savedAuth) {
      setAuthHeader(savedAuth);
      setIsLoggedIn(true);
    }
  }, []);

  // Fetch data when logged in
  useEffect(() => {
    if (isLoggedIn && authHeader) {
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const [businessesRes, statsRes, submissionsRes] = await Promise.all([
            axios.get(`${API}/admin/businesses`, {
              headers: { Authorization: authHeader },
            }),
            axios.get(`${API}/stats`),
            axios.get(`${API}/admin/submissions`, {
              headers: { Authorization: authHeader },
            }),
          ]);
          setBusinesses(businessesRes.data);
          setStats(statsRes.data);
          setSubmissions(submissionsRes.data.submissions || []);
        } catch (error) {
          console.error("Error fetching data:", error);
          if (error.response?.status === 401) {
            setIsLoggedIn(false);
            setAuthHeader("");
            localStorage.removeItem("adminAuth");
            setCredentials({ email: "", password: "" });
          }
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [isLoggedIn, authHeader]);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const [businessesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/businesses`, {
          headers: { Authorization: authHeader },
        }),
        axios.get(`${API}/stats`),
      ]);
      setBusinesses(businessesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  // POD Sync Functions
  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/sync/status`, {
        headers: { Authorization: authHeader },
      });
      setSyncStatus(response.data);
    } catch (error) {
      console.error("Error fetching sync status:", error);
    }
  };

  const triggerSync = async (type) => {
    setSyncLoading((prev) => ({ ...prev, [type]: true }));
    setSyncResult(null);
    try {
      const endpoint = type === "all" ? "/admin/sync/all" : `/admin/sync/${type}`;
      const response = await axios.post(`${API}${endpoint}`, {}, {
        headers: { Authorization: authHeader },
      });
      setSyncResult({ type, success: true, data: response.data });
      fetchSyncStatus();
      fetchDashboardData();
    } catch (error) {
      setSyncResult({ type, success: false, error: error.response?.data?.detail || "Sync failed" });
    } finally {
      setSyncLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  // Fetch sync status when logged in
  useEffect(() => {
    if (isLoggedIn && authHeader) {
      fetchSyncStatus();
    }
  }, [isLoggedIn, authHeader]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setIsLoading(true);

    const auth = btoa(`${credentials.email}:${credentials.password}`);
    const header = `Basic ${auth}`;

    try {
      await axios.post(
        `${API}/admin/login`,
        {},
        { headers: { Authorization: header } }
      );
      setAuthHeader(header);
      localStorage.setItem("adminAuth", header);
      setIsLoggedIn(true);
    } catch (error) {
      setLoginError("Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setAuthHeader("");
    localStorage.removeItem("adminAuth");
    setCredentials({ email: "", password: "" });
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file && file.name.endsWith(".csv")) {
        uploadCSV(file);
      } else {
        setUploadResult({ error: "Please upload a CSV file" });
      }
    },
    [authHeader]
  );

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadCSV(file);
    }
  };

  const uploadCSV = async (file) => {
    setIsUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API}/admin/import-businesses`,
        formData,
        {
          headers: {
            Authorization: authHeader,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setUploadResult(response.data);
      fetchDashboardData(); // Refresh data
    } catch (error) {
      setUploadResult({
        error: error.response?.data?.detail || "Upload failed",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleApproveClaim = async (businessId) => {
    setActionLoading((prev) => ({ ...prev, [businessId]: "approve" }));
    try {
      await axios.post(
        `${API}/admin/approve-claim/${businessId}`,
        {},
        { headers: { Authorization: authHeader } }
      );
      fetchDashboardData();
    } catch (error) {
      console.error("Error approving claim:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [businessId]: null }));
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!window.confirm("Are you sure you want to delete this business?")) {
      return;
    }
    setActionLoading((prev) => ({ ...prev, [businessId]: "delete" }));
    try {
      await axios.delete(`${API}/admin/business/${businessId}`, {
        headers: { Authorization: authHeader },
      });
      fetchDashboardData();
    } catch (error) {
      console.error("Error deleting business:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [businessId]: null }));
    }
  };

  // Handle submission approval
  const handleApproveSubmission = async (businessId) => {
    setActionLoading((prev) => ({ ...prev, [businessId]: "approve" }));
    try {
      await axios.post(
        `${API}/admin/submissions/${businessId}/approve`,
        {},
        { headers: { Authorization: authHeader } }
      );
      // Refresh submissions
      const res = await axios.get(`${API}/admin/submissions`, {
        headers: { Authorization: authHeader },
      });
      setSubmissions(res.data.submissions || []);
      fetchDashboardData();
    } catch (error) {
      console.error("Error approving submission:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [businessId]: null }));
    }
  };

  // Handle submission rejection
  const handleRejectSubmission = async (businessId) => {
    if (!window.confirm("Are you sure you want to reject this submission?")) {
      return;
    }
    setActionLoading((prev) => ({ ...prev, [businessId]: "reject" }));
    try {
      await axios.post(
        `${API}/admin/submissions/${businessId}/reject`,
        {},
        { headers: { Authorization: authHeader } }
      );
      // Refresh submissions
      const res = await axios.get(`${API}/admin/submissions`, {
        headers: { Authorization: authHeader },
      });
      setSubmissions(res.data.submissions || []);
      fetchDashboardData();
    } catch (error) {
      console.error("Error rejecting submission:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [businessId]: null }));
    }
  };

  // Filter businesses
  const filteredBusinesses = businesses.filter((biz) => {
    const matchesSearch =
      biz.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      biz.category_name.toLowerCase().includes(searchQuery.toLowerCase());

    if (filterStatus === "all") return matchesSearch;
    if (filterStatus === "claimed") return matchesSearch && biz.is_claimed;
    if (filterStatus === "unclaimed") return matchesSearch && !biz.is_claimed;
    if (filterStatus === "pending")
      return matchesSearch && biz.claim_status === "pending";
    if (filterStatus === "best") return matchesSearch && biz.is_best_of_best;
    return matchesSearch;
  });

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#020817] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 rounded-2xl w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#00E5FF] flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold font-['Outfit'] text-white">
              Admin Dashboard
            </h1>
            <p className="text-[#94A3B8] mt-2">
              Sign in to manage the directory
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm text-[#94A3B8] mb-2">Email</label>
              <Input
                type="email"
                value={credentials.email}
                onChange={(e) =>
                  setCredentials({ ...credentials, email: e.target.value })
                }
                placeholder="admin@example.com"
                className="bg-white/5 border-white/10 text-white"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="block text-sm text-[#94A3B8] mb-2">
                Password
              </label>
              <Input
                type="password"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials({ ...credentials, password: e.target.value })
                }
                placeholder="••••••••"
                className="bg-white/5 border-white/10 text-white"
                required
                data-testid="admin-password-input"
              />
            </div>

            {loginError && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                {loginError}
              </div>
            )}

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={isLoading}
              data-testid="admin-login-btn"
            >
              {isLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Shield className="w-4 h-4 mr-2" />
              )}
              Sign In
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Dashboard Screen
  return (
    <div className="min-h-screen bg-[#020817] pt-20 pb-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold font-['Outfit'] text-white">
              Admin Dashboard
            </h1>
            <p className="text-[#94A3B8]">
              Manage businesses, claims, and imports
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="btn-secondary"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="glass-card p-4 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#7C3AED]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {stats.businesses}
                  </p>
                  <p className="text-xs text-[#94A3B8]">Total Businesses</p>
                </div>
              </div>
            </div>
            <div className="glass-card p-4 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {stats.claimed_businesses || 0}
                  </p>
                  <p className="text-xs text-[#94A3B8]">Claimed</p>
                </div>
              </div>
            </div>
            <div className="glass-card p-4 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {stats.unclaimed_businesses || 0}
                  </p>
                  <p className="text-xs text-[#94A3B8]">Unclaimed</p>
                </div>
              </div>
            </div>
            <div className="glass-card p-4 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#00E5FF]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {stats.best_of_best || 0}
                  </p>
                  <p className="text-xs text-[#94A3B8]">Best of Best</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI Search Market Demand */}
        <MarketDemandWidget authHeader={authHeader} />

        {/* CSV Upload Section */}
        <div className="glass-card p-6 rounded-2xl mb-8">
          <div className="flex items-center gap-3 mb-4">
            <FileSpreadsheet className="w-6 h-6 text-[#7C3AED]" />
            <h2 className="text-xl font-bold font-['Outfit'] text-white">
              Bulk Import Businesses
            </h2>
          </div>

          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              isDragging
                ? "border-[#7C3AED] bg-[#7C3AED]/10"
                : "border-white/10 hover:border-white/20"
            }`}
          >
            <Upload
              className={`w-12 h-12 mx-auto mb-4 ${
                isDragging ? "text-[#7C3AED]" : "text-[#94A3B8]"
              }`}
            />
            <p className="text-white font-medium mb-2">
              {isDragging
                ? "Drop your CSV file here"
                : "Drag & drop your CSV file here"}
            </p>
            <p className="text-[#94A3B8] text-sm mb-4">or</p>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="csv-file-input"
              />
              <Button
                type="button"
                variant="outline"
                className="btn-secondary"
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Select CSV File
                  </>
                )}
              </Button>
            </label>
          </div>

          {/* CSV Format Guide */}
          <div className="mt-4 p-4 rounded-lg bg-white/5">
            <p className="text-sm text-[#94A3B8] mb-2">
              <strong className="text-white">CSV Format:</strong>
            </p>
            <code className="text-xs text-[#00E5FF] block overflow-x-auto">
              name,category,description,website,phone,email,city,state,services
            </code>
          </div>

          {/* Upload Result */}
          {uploadResult && (
            <div
              className={`mt-4 p-4 rounded-lg ${
                uploadResult.error
                  ? "bg-red-500/10 border border-red-500/20"
                  : "bg-green-500/10 border border-green-500/20"
              }`}
            >
              {uploadResult.error ? (
                <div className="flex items-center gap-2 text-red-400">
                  <XCircle className="w-5 h-5" />
                  {uploadResult.error}
                </div>
              ) : (
                <div className="text-green-400">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Import Complete!
                  </div>
                  <p className="text-sm">
                    {uploadResult.successful} of {uploadResult.total_rows}{" "}
                    businesses imported successfully
                  </p>
                  {uploadResult.errors?.length > 0 && (
                    <div className="mt-2 text-xs text-red-400">
                      {uploadResult.errors.slice(0, 3).map((err, i) => (
                        <p key={i}>{err}</p>
                      ))}
                      {uploadResult.errors.length > 3 && (
                        <p>...and {uploadResult.errors.length - 3} more errors</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* POD Sync Panel */}
        <div className="glass-card p-6 rounded-2xl mb-8" data-testid="pod-sync-panel">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#00E5FF] flex items-center justify-center">
                <Database className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold font-['Outfit'] text-white">
                  POD Data Sync
                </h2>
                <p className="text-sm text-[#94A3B8]">
                  Sync businesses & content from Partner on Demand
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchSyncStatus}
              className="text-[#94A3B8] hover:text-white"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
          </div>

          {/* Sync Status */}
          {syncStatus && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-[#7C3AED]" />
                  <span className="text-sm text-[#94A3B8]">Businesses</span>
                </div>
                <p className="text-2xl font-bold text-white">{syncStatus.businesses?.total || 0}</p>
                <p className="text-xs text-[#94A3B8]">
                  {syncStatus.businesses?.synced_from_pod || 0} from POD
                </p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <FileSpreadsheet className="w-4 h-4 text-[#00E5FF]" />
                  <span className="text-sm text-[#94A3B8]">Content Items</span>
                </div>
                <p className="text-2xl font-bold text-white">{syncStatus.content?.total || 0}</p>
                <p className="text-xs text-[#94A3B8]">
                  {syncStatus.content?.synced_from_pod || 0} from POD
                </p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-[#F59E0B]" />
                  <span className="text-sm text-[#94A3B8]">Last Business Sync</span>
                </div>
                <p className="text-sm text-white">
                  {syncStatus.last_business_sync 
                    ? new Date(syncStatus.last_business_sync).toLocaleString()
                    : "Never"}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-[#10B981]" />
                  <span className="text-sm text-[#94A3B8]">Last Content Sync</span>
                </div>
                <p className="text-sm text-white">
                  {syncStatus.last_content_sync 
                    ? new Date(syncStatus.last_content_sync).toLocaleString()
                    : "Never"}
                </p>
              </div>
            </div>
          )}

          {/* Sync Buttons */}
          <div className="flex flex-wrap gap-3 mb-4">
            <Button
              onClick={() => triggerSync("businesses")}
              disabled={syncLoading.businesses || syncLoading.all}
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
              data-testid="sync-businesses-btn"
            >
              {syncLoading.businesses ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CloudDownload className="w-4 h-4 mr-2" />
              )}
              Sync Businesses
            </Button>
            <Button
              onClick={() => triggerSync("content")}
              disabled={syncLoading.content || syncLoading.all}
              className="bg-[#00E5FF] hover:bg-[#00D4ED] text-black"
              data-testid="sync-content-btn"
            >
              {syncLoading.content ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CloudDownload className="w-4 h-4 mr-2" />
              )}
              Sync Content
            </Button>
            <Button
              onClick={() => triggerSync("all")}
              disabled={syncLoading.businesses || syncLoading.content || syncLoading.all}
              variant="outline"
              className="border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/10"
              data-testid="sync-all-btn"
            >
              {syncLoading.all ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Sync All
            </Button>
          </div>

          {/* Sync Result */}
          {syncResult && (
            <div
              className={`p-4 rounded-lg ${
                syncResult.success
                  ? "bg-green-500/10 border border-green-500/20"
                  : "bg-red-500/10 border border-red-500/20"
              }`}
            >
              {syncResult.success ? (
                <div className="text-green-400">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Sync Complete!</span>
                  </div>
                  {syncResult.type === "all" ? (
                    <div className="text-sm space-y-1">
                      <p>Businesses: {syncResult.data?.businesses?.synced_count || 0} synced</p>
                      <p>Content: {syncResult.data?.content?.synced_count || 0} synced</p>
                    </div>
                  ) : (
                    <p className="text-sm">
                      {syncResult.data?.synced_count || 0} items synced from POD
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-400">
                  <XCircle className="w-5 h-5" />
                  <span>{syncResult.error}</span>
                </div>
              )}
            </div>
          )}

          {/* POD Info */}
          <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-[#00E5FF]" />
              <span className="text-sm font-medium text-white">POD Connection</span>
            </div>
            <div className="text-xs text-[#94A3B8] space-y-1">
              <p><span className="text-[#7C3AED]">API:</span> {syncStatus?.pod_api_url || "Loading..."}</p>
              <p><span className="text-[#10B981]">Webhook:</span> {syncStatus?.pod_webhook_url || "Loading..."}</p>
              <p className="text-[#F59E0B] mt-2">
                ⚡ Background sync runs automatically every 15 min (businesses) and 5 min (content)
              </p>
            </div>
          </div>
        </div>

        {/* Business Submissions Section */}
        {submissions.length > 0 && (
          <div className="glass-card p-6 rounded-2xl mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold font-['Outfit'] text-white">
                    Business Submissions
                  </h2>
                  <p className="text-sm text-[#94A3B8]">
                    Self-submitted listings pending review ({submissions.filter(s => s.submission_status === "pending_review").length} pending)
                  </p>
                </div>
              </div>
              <span className="px-3 py-1 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] text-sm">
                {submissions.length} total
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-[#94A3B8] text-sm font-medium">Business</th>
                    <th className="text-left py-3 px-4 text-[#94A3B8] text-sm font-medium">Contact</th>
                    <th className="text-left py-3 px-4 text-[#94A3B8] text-sm font-medium">Category</th>
                    <th className="text-left py-3 px-4 text-[#94A3B8] text-sm font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-[#94A3B8] text-sm font-medium">Submitted</th>
                    <th className="text-right py-3 px-4 text-[#94A3B8] text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => (
                    <tr key={submission.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={submission.logo_url || `https://api.dicebear.com/7.x/shapes/svg?seed=${submission.slug}`}
                            alt={submission.name}
                            className="w-10 h-10 rounded-lg object-cover bg-white/10"
                          />
                          <div>
                            <p className="text-white font-medium">{submission.name}</p>
                            <p className="text-[#94A3B8] text-xs">{submission.website || "No website"}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-white text-sm">{submission.contact_name || "N/A"}</p>
                        <p className="text-[#94A3B8] text-xs">{submission.contact_email}</p>
                        {submission.contact_phone && (
                          <p className="text-[#94A3B8] text-xs">{submission.contact_phone}</p>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-[#94A3B8] text-sm">{submission.category_name}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          submission.submission_status === "pending_review"
                            ? "bg-[#F59E0B]/10 text-[#F59E0B]"
                            : submission.submission_status === "approved"
                            ? "bg-[#10B981]/10 text-[#10B981]"
                            : "bg-red-500/10 text-red-400"
                        }`}>
                          {submission.submission_status === "pending_review" ? "Pending" : 
                           submission.submission_status === "approved" ? "Approved" : "Rejected"}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-[#94A3B8] text-xs">
                          {submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString() : "N/A"}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={`/directory/${submission.category_id}/${submission.city ? submission.city.toLowerCase().replace(/\s+/g, "-") + "-" + (submission.state || "").toLowerCase().replace(/\s+/g, "-") : "local"}/${submission.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button variant="ghost" size="sm" className="text-[#94A3B8] hover:text-white">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </a>
                          {submission.submission_status === "pending_review" && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApproveSubmission(submission.id)}
                                disabled={actionLoading[submission.id] === "approve"}
                                className="text-[#10B981] hover:bg-[#10B981]/10"
                              >
                                {actionLoading[submission.id] === "approve" ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRejectSubmission(submission.id)}
                                disabled={actionLoading[submission.id] === "reject"}
                                className="text-red-400 hover:bg-red-500/10"
                              >
                                {actionLoading[submission.id] === "reject" ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <X className="w-4 h-4" />
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Business List */}
        <div className="glass-card p-6 rounded-2xl">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-[#00E5FF]" />
              <h2 className="text-xl font-bold font-['Outfit'] text-white">
                All Businesses ({filteredBusinesses.length})
              </h2>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search businesses..."
                  className="pl-10 bg-white/5 border-white/10 text-white w-full sm:w-64"
                />
              </div>

              {/* Filter */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
              >
                <option value="all">All Status</option>
                <option value="claimed">Claimed</option>
                <option value="unclaimed">Unclaimed</option>
                <option value="pending">Pending Claims</option>
                <option value="best">Best of Best</option>
              </select>

              {/* Refresh */}
              <Button
                variant="ghost"
                size="icon"
                onClick={fetchDashboardData}
                disabled={isLoading}
                className="text-[#94A3B8] hover:text-white"
              >
                <RefreshCw
                  className={`w-5 h-5 ${isLoading ? "animate-spin" : ""}`}
                />
              </Button>
            </div>
          </div>

          {/* Business Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-[#94A3B8] text-sm font-medium">
                    Business
                  </th>
                  <th className="text-left py-3 px-4 text-[#94A3B8] text-sm font-medium">
                    Category
                  </th>
                  <th className="text-left py-3 px-4 text-[#94A3B8] text-sm font-medium">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 text-[#94A3B8] text-sm font-medium">
                    Location
                  </th>
                  <th className="text-right py-3 px-4 text-[#94A3B8] text-sm font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredBusinesses.map((business) => (
                  <tr
                    key={business.id}
                    className="border-b border-white/5 hover:bg-white/5"
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={business.logo_url}
                          alt={business.name}
                          className="w-10 h-10 rounded-lg object-cover bg-white/5"
                        />
                        <div>
                          <p className="text-white font-medium">
                            {business.name}
                          </p>
                          <p className="text-[#94A3B8] text-xs">
                            {business.ai_agent_name} • AI Hero
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-[#7C3AED] text-sm">
                        {business.category_name}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-wrap gap-1">
                        {business.is_best_of_best && (
                          <span className="px-2 py-0.5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs">
                            Best of Best
                          </span>
                        )}
                        {business.is_claimed ? (
                          <span className="px-2 py-0.5 rounded-full bg-[#10B981]/20 text-[#10B981] text-xs">
                            Claimed
                          </span>
                        ) : business.claim_status === "pending" ? (
                          <span className="px-2 py-0.5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs">
                            Pending
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-[#94A3B8]/20 text-[#94A3B8] text-xs">
                            Unclaimed
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-[#94A3B8] text-sm">
                        {business.city && business.state
                          ? `${business.city}, ${business.state}`
                          : "—"}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        {business.claim_status === "pending" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleApproveClaim(business.id)}
                            disabled={actionLoading[business.id]}
                            className="text-[#10B981] hover:text-[#10B981] hover:bg-[#10B981]/10"
                          >
                            {actionLoading[business.id] === "approve" ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        <a
                          href={`/business/${business.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-[#94A3B8] hover:text-white"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </a>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteBusiness(business.id)}
                          disabled={actionLoading[business.id]}
                          className="text-red-400 hover:text-red-400 hover:bg-red-500/10"
                        >
                          {actionLoading[business.id] === "delete" ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredBusinesses.length === 0 && (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                <p className="text-[#94A3B8]">No businesses found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
